import {Box, Typography, Chip, List, ListItem, ListItemText} from '@mui/material';
import {SessionQuestion} from '@cp/model';
import MarkdownContentRenderer from '../../atoms/MarkdownContentRenderer/MarkdownContentRenderer';

interface QuestionReviewQuestionOptionsProps {
  question: SessionQuestion;
}

export default function QuestionReviewQuestionOptions({question}: QuestionReviewQuestionOptionsProps) {
  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{mb: 1, fontWeight: 500}}>
        Options:
      </Typography>
      <List dense>
        {question.options.map((option, optionIndex) => {
          const isSelected = option === question.selectedAnswer;
          const isCorrect = option === question.answer;
          const isSelectedAndCorrect = isSelected && isCorrect;
          const isSelectedAndWrong = isSelected && !isCorrect;

          // Calculate background color
          let bgcolor = 'transparent';
          if (isSelectedAndCorrect) {
            bgcolor = 'success.50';
          } else if (isSelectedAndWrong) {
            bgcolor = 'error.50';
          } else if (isCorrect) {
            bgcolor = 'success.25';
          }

          // Calculate border color
          let borderColor = 'divider';
          if (isSelectedAndCorrect) {
            borderColor = 'success.main';
          } else if (isSelectedAndWrong) {
            borderColor = 'error.main';
          } else if (isCorrect) {
            borderColor = 'success.light';
          }

          return (
            <ListItem
              key={`option-${option.slice(0, 10) || optionIndex}`}
              sx={{
                py: {xs: 0.75, sm: 0.5},
                px: {xs: 1.5, sm: 2},
                borderRadius: 1,
                bgcolor,
                border: isSelected ? '2px solid' : '1px solid',
                borderColor,
                mb: 0.5,
              }}
            >
              <ListItemText
                primary={
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1,
                      flexDirection: {xs: 'column', sm: 'row'},
                    }}
                  >
                    <Typography
                      component="div"
                      variant="body2"
                      sx={{
                        flex: 1,
                        minWidth: 0,
                        wordBreak: 'break-word',
                        lineHeight: 1.4,
                      }}
                    >
                      <MarkdownContentRenderer content={option} />
                    </Typography>
                    <Box sx={{display: 'flex', gap: 0.5, flexWrap: 'wrap', flexShrink: 0}}>
                      {isSelected && (
                        <Chip
                          label={isCorrect ? 'Your Answer ✓' : 'Your Answer ✗'}
                          size="small"
                          color={isCorrect ? 'success' : 'error'}
                          variant="filled"
                        />
                      )}
                      {isCorrect && !isSelected && (
                        <Chip label="Correct Answer" size="small" color="success" variant="outlined" />
                      )}
                    </Box>
                  </Box>
                }
              />
            </ListItem>
          );
        })}
      </List>
    </Box>
  );
}
